import { Box, Popover, Typography, useTheme } from "@mui/material";
import { Suspense, useContext, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { ContentfulContext } from "../../context/ContentfulContext";
import { LanguageContext } from "../../context/LanguageContext";
import { Header, Language } from "../../models/schema";
import { colors } from "../../theme/colors";
import Button from "./Button";

const FlagIcon = ({ language, size }: { language: Language; size: number | string }) => {
  return (
    <CircleFlag
      countryCode={language.flag as string}
      style={{
        width: size,
        height: size
      }}
    />
  );
};

const LanguageButtons = ({
  availableLanguages,
  activeLanguage,
  onClick
}: {
  availableLanguages: Language[];
  activeLanguage: Language;
  onClick: (lang: Language) => void;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        width: { xs: "100%", md: "auto" },
        "& > :not(template) ~ :not(template)": {
          ml: 2
        },
        justifyContent: "center"
      }}>
      {availableLanguages.map((lang) => (
        <Button
          onClick={() => onClick(lang)}
          key={lang.code}
          variant="text"
          startIcon={<FlagIcon language={lang} size={theme.spacing(8)} />}
          sx={{
            // width: theme.spacing(8),
            // height: theme.spacing(8),
            filter: activeLanguage.code === lang.code ? "none" : "grayscale(1)"
          }}
        />
      ))}
    </Box>
  );
};

export const LanguageSelector = ({
  mobile,
  setMobileNavOpen
}: {
  mobile?: boolean;
  setMobileNavOpen?: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClick = (lang: Language) => {
    languageCtx.setLanguage(lang.code as string);
    handleClose();
    setMobileNavOpen?.();

    // GGOGLE RECAPTCHA currently not used in the app
    // We need to refresh the page in order to change the language everywhere ( eg. Google Recaptcha )
    // window.location.reload();
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const languageCtx = useContext(LanguageContext);
  const availableLanguages = languageCtx.availableLanguages;

  const language = availableLanguages?.find((lang) => lang.code === languageCtx.selectedLanguage);

  const { content } = useContext(ContentfulContext);

  const { language: languageLabel } = content?.header as Header;

  return (
    <Suspense fallback={<></>}>
      {availableLanguages?.length > 1 && (
        <>
          {mobile && (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center"
              }}>
              <Typography variant="h5" mb={3}>
                {languageLabel}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  "& > :not(template) ~ :not(template)": {
                    mt: 2
                  }
                }}>
                <LanguageButtons
                  onClick={onClick}
                  activeLanguage={language as Language}
                  availableLanguages={availableLanguages}
                />
              </Box>
            </Box>
          )}

          {!mobile && (
            <>
              <Button
                disableRipple={true}
                onClick={handleOpen}
                label={languageCtx.selectedLanguage?.toLocaleUpperCase()}
                variant="text"
                sx={{
                  p: 1,
                  color: open ? colors.yellow : colors.white,
                  "&:hover": {
                    textDecoration: "underline"
                  },
                  "&:active": {
                    color: colors.yellow
                  },
                  "&.Mui-focusVisible": {
                    outline: `${colors.white} solid 1px`
                  },
                  "& p": {
                    margin: 0,
                    fontWeight: 300
                  }
                }}
              />
              <Popover
                id={id}
                open={open}
                onClose={handleClose}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
                sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "& > :not(template) ~ :not(template)": {
                      mt: 2
                    },
                    justifyContent: "center"
                  }}>
                  <LanguageButtons
                    onClick={onClick}
                    activeLanguage={language as Language}
                    availableLanguages={availableLanguages}
                  />
                </Box>
              </Popover>
            </>
          )}
        </>
      )}
    </Suspense>
  );
};
